import React, { useEffect, useState } from 'react';

interface ScoreGaugeProps {
  score: number;
}

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score }) => {
  const [displayedScore, setDisplayedScore] = useState(0);
  const size = 160;
  const strokeWidth = 2;
  const center = size / 2;
  const radius = center - 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (displayedScore / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => setDisplayedScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);

  const getColor = (s: number) => {
    if (s < 40) return 'rgb(244 63 94)'; // Rose
    if (s < 75) return 'rgb(250 204 21)'; // Yellow
    return 'rgb(34 211 238)'; // Cyan
  };

  const currentColor = getColor(displayedScore);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Spinning Outer Ring */}
      <div className="absolute inset-0 border-2 border-dashed border-white/5 rounded-full animate-[spin_10s_linear_infinite]"></div>
      
      {/* Tactical Compass Accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-2 bg-white/20"></div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1px] h-2 bg-white/20"></div>
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-[1px] bg-white/20"></div>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-[1px] bg-white/20"></div>

      <svg width={size} height={size} className="transform -rotate-90 drop-shadow-2xl">
        {/* Background Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="rgba(255,255,255,0.03)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress Path */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={currentColor}
          strokeWidth={4}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          fill="none"
          className="transition-all duration-1000 ease-out"
          style={{ filter: `drop-shadow(0 0 8px ${currentColor})` }}
        />
        
        {/* Inner Data Ring */}
        <circle
            cx={center}
            cy={center}
            r={radius - 12}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={1}
            strokeDasharray="4 8"
            fill="none"
        />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[10px] font-mono font-black text-subtext uppercase tracking-[0.3em] mb-1">Humanity</span>
        <span className="text-4xl font-mono font-black tracking-tighter transition-colors duration-500" style={{ color: currentColor }}>
            {displayedScore}%
        </span>
        <div className="mt-2 w-8 h-[1px] bg-white/10"></div>
      </div>
    </div>
  );
};